import type { Room } from '@hollowveil/shared';

/**
 * Wonderland - "Le Chapelier qui ne riait plus"
 *
 * Personnage à aider: Le Chapelier Fou
 * Problématique: Anxiété sociale sévère. Il n'arrive plus à organiser ses tea parties.
 *
 * Ce que Côtelette apprend: L'écoute active
 */
export const roomWonderlandReveil: Room = {
  id: 'room_wonderland_reveil',
  name: 'Wonderland - Le Chapelier qui ne riait plus',
  description: 'Un jardin victorien autrefois joyeux, maintenant empli de tristesse. Le Chapelier se cache sous sa table.',
  size: [24, 10, 24],
  floorStyle: 'wonderland',
  palette: 'forest',
  initialWorld: 'real',
  hasSpectralLayer: true,
  spawnPosition: [6, 0, 6], // Spawn plus loin du portail retour
  ambiance: {
    realWorld: {
      backgroundColor: '#87CEEB',
      ambientLightColor: '#FFF8DC',
      ambientLightIntensity: 0.7, // Un peu plus sombre qu'avant - la tristesse
      fogColor: '#c0c0c0',
      fogDensity: 0.02,
    },
    spectralWorld: {
      // Wonderland tordu - on voit les pensées anxieuses
      backgroundColor: '#2a0a3a',
      ambientLightColor: '#ff69b4',
      ambientLightIntensity: 0.4,
      fogColor: '#4a1a6a',
      fogDensity: 0.05,
    },
  },
  objects: [
    // === TABLE DE THÉ VIDE ET TRISTE ===
    {
      id: 'obj_tea_table_sad',
      name: 'Table de Thé Abandonnée',
      type: 'interactive',
      modelId: 'tea_table',
      position: [12, 0, 12],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { state: 'abandoned' },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une table de tea party... les tasses sont renversées, le thé est froid depuis longtemps.',
          },
        },
      ],
    },

    // Le Chapelier dans le monde RÉEL - Anxieux, pendant la tea party
    // Disparaît du réel après avoir validé la tea party (hatter_validate)
    {
      id: 'obj_hatter',
      name: 'Le Chapelier Fou',
      type: 'interactive',
      modelId: 'npc_mad_hatter',
      position: [12, 0, 11],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        state: 'anxious',
        dialogueId: 'hatter_dynamic',
        hideAfterRiddleSolved: 'hatter_validate', // Disparaît du réel après validation
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: {
            riddleType: 'hatter',
          },
        },
      ],
    },

    // Le Chapelier dans le monde SPECTRAL - Apaisé, après la tea party
    // Apparaît seulement après avoir validé la tea party
    {
      id: 'obj_hatter_spectral',
      name: 'Le Chapelier Apaisé',
      type: 'interactive',
      modelId: 'npc_mad_hatter',
      position: [12, 0, 11],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isNPC: true,
        state: 'calm', // État différent - calme
        dialogueId: 'hatter_spectral',
        showAfterRiddleSolved: 'hatter_validate', // Apparaît après validation
        glows: true, // Effet lumineux pour montrer sa transformation
      },
      onInteract: [
        {
          type: 'showDynamicRiddle',
          params: {
            riddleType: 'hatter_spectral',
          },
        },
      ],
    },

    // Pensées anxieuses du Chapelier (visibles en spectral)
    {
      id: 'obj_anxiety_bubble_1',
      name: 'Pensée Anxieuse',
      type: 'static',
      modelId: 'thought_bubble',
      position: [11, 2, 12],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        text: 'Ils vont me juger...',
        color: '#ff0000',
      },
    },
    {
      id: 'obj_anxiety_bubble_2',
      name: 'Pensée Anxieuse',
      type: 'static',
      modelId: 'thought_bubble',
      position: [13, 2.5, 11],
      rotation: [0, 0.5, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        text: 'Je vais tout rater...',
        color: '#ff0000',
      },
    },
    {
      id: 'obj_anxiety_bubble_3',
      name: 'Pensée Anxieuse',
      type: 'static',
      modelId: 'thought_bubble',
      position: [12, 3, 13],
      rotation: [0, -0.3, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        text: 'Personne ne m\'aime...',
        color: '#ff0000',
      },
    },

    // === SOUVENIRS À TROUVER ===

    // Souvenir 1: Photo cachée sous un champignon
    {
      id: 'obj_memory_photo_1',
      name: 'Photo de Tea Party',
      type: 'collectible',
      modelId: 'photo_frame',
      position: [5, 0, 8],
      rotation: [0, 0.2, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        memoryType: 'happy',
        description: 'Une photo où le Chapelier rit aux éclats avec ses amis.',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'memory_photo_1',
            itemName: 'Souvenir Heureux #1',
            itemDescription: 'Une photo de tea party où tout le monde riait.',
            itemIcon: 'scroll',
          },
        },
        {
          type: 'showMessage',
          params: {
            message: 'Tu trouves une vieille photo... Le Chapelier sourit dessus, entouré d\'amis.',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_memory_photo_1' },
        },
      ],
    },

    // Le Lièvre de Mars - donne le souvenir #2
    {
      id: 'obj_march_hare',
      name: 'Le Lièvre de Mars',
      type: 'interactive',
      modelId: 'npc_march_hare',
      position: [19, 0, 5],
      rotation: [0, Math.PI, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        dialogueId: 'march_hare_dialogue',
      },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddle: {
              id: 'march_hare_memory',
              npcId: 'obj_march_hare',
              npcName: 'Le Lièvre de Mars',
              question: `*sursaute et renverse sa tasse*

"OH ! Une visiteuse ! Excuse le désordre, je... je n'attendais personne.

Tu cherches à aider le Chapelier ? *baisse les oreilles*

Il... il ne va pas bien depuis la dernière fête. Il pense qu'il a tout raté.

Mais c'était GÉNIAL ! J'ai même gardé la lettre que je lui avais écrite après...

*fouille dans sa veste* Tiens ! Donne-lui ça. Peut-être que ça lui rappellera de bons souvenirs ?"`,
              type: 'multiple_choice',
              choices: [
                { id: 'a', text: 'Merci, je vais lui donner !', isCorrect: true },
                { id: 'b', text: "C'était une belle fête ?", isCorrect: true },
              ],
              correctResponse: `"La MEILLEURE ! On a ri pendant des heures. Le Chapelier avait fait des chapeaux pour tout le monde...

*essuie une larme* Il était tellement heureux ce jour-là.

Dis-lui... dis-lui qu'il me manque. Et que ses tea parties me manquent aussi."`,
              incorrectResponse: '',
              canRetry: true,
              onCorrect: [
                {
                  type: 'addToInventory',
                  params: {
                    itemId: 'memory_photo_2',
                    itemName: 'Souvenir Heureux #2',
                    itemDescription: 'Une lettre du Lièvre: "Mon cher ami, ta fête était INCROYABLE!"',
                    itemIcon: 'scroll',
                  },
                },
              ],
            },
          },
        },
      ],
    },

    // Le Chat du Cheshire
    {
      id: 'obj_cheshire_cat',
      name: 'Le Chat du Cheshire',
      type: 'interactive',
      modelId: 'npc_cheshire_cat',
      position: [8, 2, 18],
      rotation: [0, -0.5, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        isNPC: true,
        hasRiddle: true,
        rewardItem: 'memory_photo_3',
      },
      onInteract: [
        {
          type: 'showRiddle',
          params: {
            riddle: {
              id: 'cheshire_riddle',
              npcId: 'obj_cheshire_cat',
              npcName: 'Le Chat du Cheshire',
              question: '*apparaît progressivement, sourire d\'abord*\n\n"Tiens, tiens... une visiteuse curieuse !\n\nTu cherches un souvenir, n\'est-ce pas ? J\'en ai un... mais tout a un prix.\n\nRéponds à mon énigme, et le souvenir sera tien :\n\nJe suis partout et nulle part, je peux faire rire ou pleurer...\nOn me cherche quand on l\'a perdu, et on me fuit quand on en a trop.\n\nQui suis-je ?"',
              hint: 'Pense à ce qui tourmente le Chapelier...',
              type: 'multiple_choice',
              choices: [
                { id: 'a', text: 'Le temps !', isCorrect: true },
                { id: 'b', text: "L'attention !", isCorrect: true },
                { id: 'c', text: 'Le regard des autres !', isCorrect: true },
              ],
              correctResponse: '"*ronronne* Excellent ! Tu comprends les choses importantes.\n\nVoici le souvenir. C\'était la meilleure tea party... parce que personne ne regardait l\'heure.\n\n*tend une photo* Le Chapelier riait tellement ce jour-là...\n\n*commence à disparaître* Au revoir... ou plutôt, à la prochaine fois que tu me chercheras !"',
              incorrectResponse: '*son sourire s\'élargit* Un indice ? Pense au Chapelier. Qu\'est-ce qui le tourmente ? Ce n\'est pas le thé qui refroidit...',
              canRetry: true,
              onCorrect: [
                {
                  type: 'addToInventory',
                  params: {
                    itemId: 'memory_photo_3',
                    itemName: 'Souvenir Heureux #3',
                    itemDescription: 'Une photo de la meilleure tea party, où le temps n\'existait pas.',
                    itemIcon: '📷',
                  },
                },
              ],
            },
          },
        },
      ],
    },

    // === PUZZLE TEA PARTY - 4 DALLES AUTOUR DE LA TABLE ===
    // Le joueur doit collecter les items et les placer sur les bonnes dalles
    // Les dalles n'apparaissent qu'après avoir parlé au Chapelier

    // Dalle 1: TASSE (Nord de la table)
    {
      id: 'obj_tea_tile_cup',
      name: 'Cercle Rose',
      type: 'interactive',
      modelId: 'block',
      position: [12, 0, 10],
      rotation: [0, 0, 0],
      scale: [1.5, 0.15, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        variant: 'accent',
        color: '#FFB6C1',
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'placeTeaItem',
          params: {
            requiredItemId: 'tea_item_cup',
            placedRiddleId: 'tea_placed_cup',
            tileName: 'Cercle Rose',
            tileColor: 'rose 🩷',
          },
        },
      ],
    },

    // Dalle 2: SOUCOUPE (Est de la table)
    {
      id: 'obj_tea_tile_saucer',
      name: 'Cercle Vert',
      type: 'interactive',
      modelId: 'block',
      position: [14, 0, 12],
      rotation: [0, 0, 0],
      scale: [1.5, 0.15, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        variant: 'accent',
        color: '#98FB98',
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'placeTeaItem',
          params: {
            requiredItemId: 'tea_item_saucer',
            placedRiddleId: 'tea_placed_saucer',
            tileName: 'Cercle Vert',
            tileColor: 'vert 💚',
          },
        },
      ],
    },

    // Dalle 3: THÉIÈRE (Sud de la table)
    {
      id: 'obj_tea_tile_teapot',
      name: 'Cercle Bleu',
      type: 'interactive',
      modelId: 'block',
      position: [12, 0, 14],
      rotation: [0, 0, 0],
      scale: [1.5, 0.15, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        variant: 'accent',
        color: '#87CEEB',
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'placeTeaItem',
          params: {
            requiredItemId: 'tea_item_teapot',
            placedRiddleId: 'tea_placed_teapot',
            tileName: 'Cercle Bleu',
            tileColor: 'bleu 💙',
          },
        },
      ],
    },

    // Dalle 4: GÂTEAU (Ouest de la table)
    {
      id: 'obj_tea_tile_cake',
      name: 'Cercle Violet',
      type: 'interactive',
      modelId: 'block',
      position: [10, 0, 12],
      rotation: [0, 0, 0],
      scale: [1.5, 0.15, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        variant: 'accent',
        color: '#DDA0DD',
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'placeTeaItem',
          params: {
            requiredItemId: 'tea_item_cake',
            placedRiddleId: 'tea_placed_cake',
            tileName: 'Cercle Violet',
            tileColor: 'violet 💜',
          },
        },
      ],
    },

    // === ITEMS VISUELS SUR LES DALLES (apparaissent quand placés) ===

    // Tasse placée sur la dalle rose
    {
      id: 'obj_placed_cup',
      name: 'Tasse Placée',
      type: 'static',
      modelId: 'tea_cup',
      position: [12, 0.15, 10],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'none',
      properties: {
        showAfterRiddleSolved: 'tea_placed_cup',
        color: '#FFB6C1',
      },
    },

    // Soucoupe placée sur la dalle verte
    {
      id: 'obj_placed_saucer',
      name: 'Soucoupe Placée',
      type: 'static',
      modelId: 'tea_saucer',
      position: [14, 0.15, 12],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'none',
      properties: {
        showAfterRiddleSolved: 'tea_placed_saucer',
        color: '#98FB98',
      },
    },

    // Théière placée sur la dalle bleue
    {
      id: 'obj_placed_teapot',
      name: 'Théière Placée',
      type: 'static',
      modelId: 'tea_teapot',
      position: [12, 0.15, 14],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'none',
      properties: {
        showAfterRiddleSolved: 'tea_placed_teapot',
        color: '#87CEEB',
      },
    },

    // Gâteau placé sur la dalle violette
    {
      id: 'obj_placed_cake',
      name: 'Gâteau Placé',
      type: 'static',
      modelId: 'tea_cake',
      position: [10, 0.15, 12],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'real',
      interactableInWorld: 'none',
      properties: {
        showAfterRiddleSolved: 'tea_placed_cake',
        color: '#DDA0DD',
      },
    },

    // Indicateur visuel au centre de la table
    {
      id: 'obj_tea_instructions',
      name: 'Instructions Tea Party',
      type: 'interactive',
      modelId: 'scroll',
      position: [12, 0.8, 12],
      rotation: [0, 0, 0],
      scale: [0.5, 0.5, 0.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { floats: true },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: '📜 "Pour une tea party parfaite, place les éléments DANS L\'ORDRE:\n\n1️⃣ Tasse (cercle rose au nord)\n2️⃣ Soucoupe (cercle vert à l\'est)\n3️⃣ Théière (cercle bleu au sud)\n4️⃣ Gâteau (cercle violet à l\'ouest)\n\nLe Chapelier ne peut gérer qu\'une chose à la fois !"',
          },
        },
      ],
    },

    // === ITEMS COLLECTIBLES POUR LA TEA PARTY ===
    // Ces items apparaissent après avoir parlé au Chapelier

    // Item 1: TASSE - près du champignon géant
    {
      id: 'obj_tea_item_cup',
      name: 'Tasse de Porcelaine',
      type: 'interactive',
      modelId: 'tea_cup',
      position: [5, 0.3, 9],
      rotation: [0, 0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        icon: '☕',
        glows: true,
        color: '#FFB6C1',
        floats: true,
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'tea_item_cup',
            itemName: 'Tasse de Porcelaine',
            itemDescription: 'Une délicate tasse décorée de motifs floraux. Parfaite pour le thé !',
            itemIcon: '☕',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_tea_item_cup' },
        },
        {
          type: 'showMessage',
          params: { message: '☕ Tu as trouvé la TASSE ! Le Chapelier sera content.' },
        },
      ],
    },

    // Item 2: SOUCOUPE - près des roses (position plus visible)
    {
      id: 'obj_tea_item_saucer',
      name: 'Soucoupe Élégante',
      type: 'interactive',
      modelId: 'tea_saucer',
      position: [17, 0.3, 18],
      rotation: [0, -0.5, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        icon: '🍽️',
        glows: true,
        color: '#98FB98',
        floats: true,
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'tea_item_saucer',
            itemName: 'Soucoupe Élégante',
            itemDescription: 'Une soucoupe assortie à la tasse. Elle tinte joliment.',
            itemIcon: '🍽️',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_tea_item_saucer' },
        },
        {
          type: 'showMessage',
          params: { message: '🍽️ Tu as trouvé la SOUCOUPE ! Elle ira parfaitement avec la tasse.' },
        },
      ],
    },

    // Item 3: THÉIÈRE - près du labyrinthe de haies
    {
      id: 'obj_tea_item_teapot',
      name: 'Théière Magique',
      type: 'interactive',
      modelId: 'tea_teapot',
      position: [3, 0.3, 16],
      rotation: [0, 0.8, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        icon: '🫖',
        glows: true,
        color: '#87CEEB',
        floats: true,
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'tea_item_teapot',
            itemName: 'Théière Magique',
            itemDescription: 'Une théière qui garde le thé toujours à la bonne température.',
            itemIcon: '🫖',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_tea_item_teapot' },
        },
        {
          type: 'showMessage',
          params: { message: '🫖 Tu as trouvé la THÉIÈRE ! Elle est encore chaude...' },
        },
      ],
    },

    // Item 4: GÂTEAU - près du Lièvre de Mars
    {
      id: 'obj_tea_item_cake',
      name: 'Gâteau Non-Anniversaire',
      type: 'interactive',
      modelId: 'tea_cake',
      position: [18, 0.3, 4],
      rotation: [0, -0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        icon: '🎂',
        glows: true,
        color: '#DDA0DD',
        floats: true,
        showAfterRiddleSolved: 'hatter_first_meeting',
      },
      onInteract: [
        {
          type: 'addToInventory',
          params: {
            itemId: 'tea_item_cake',
            itemName: 'Gâteau Non-Anniversaire',
            itemDescription: 'Un délicieux gâteau pour célébrer tous les jours qui ne sont pas ton anniversaire !',
            itemIcon: '🎂',
          },
        },
        {
          type: 'hideObject',
          params: { objectId: 'obj_tea_item_cake' },
        },
        {
          type: 'showMessage',
          params: { message: '🎂 Tu as trouvé le GÂTEAU ! Joyeux non-anniversaire !' },
        },
      ],
    },

    // === DÉCORS ===

    // Haies
    {
      id: 'obj_hedge_1',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [2, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [2, 2] },
    },
    {
      id: 'obj_hedge_2',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [6, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [2, 2] },
    },
    {
      id: 'obj_hedge_3',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [18, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [2, 2] },
    },
    {
      id: 'obj_hedge_4',
      name: 'Haie Taillée',
      type: 'static',
      modelId: 'hedge',
      position: [22, 0, 2],
      rotation: [0, 0, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
      collider: { type: 'box', size: [2, 2] },
    },

    // Haies spectrales (tordues, effrayantes)
    {
      id: 'obj_hedge_spectral_1',
      name: 'Haie Tordue',
      type: 'static',
      modelId: 'hedge_twisted',
      position: [2, 0, 2],
      rotation: [0.1, 0.2, 0.1],
      scale: [2.2, 2.5, 2.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
      collider: { type: 'box', size: [2.5, 2.5] },
    },

    // Champignons géants
    {
      id: 'obj_mushroom_1',
      name: 'Champignon Géant',
      type: 'static',
      modelId: 'giant_mushroom',
      position: [4, 0, 8],
      rotation: [0, 0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_mushroom_2',
      name: 'Champignon Géant',
      type: 'static',
      modelId: 'giant_mushroom',
      position: [20, 0, 18],
      rotation: [0, -0.3, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Roses (blanches en réel, rouges sang en spectral)
    {
      id: 'obj_roses_white',
      name: 'Roses Blanches',
      type: 'static',
      modelId: 'rose_bush',
      position: [15, 0, 20],
      rotation: [0, 0, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { color: 'white' },
    },
    {
      id: 'obj_roses_red',
      name: 'Roses Rouges Sang',
      type: 'static',
      modelId: 'rose_bush',
      position: [15, 0, 20],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { color: 'blood_red' },
    },

    // Cadran solaire
    {
      id: 'obj_sundial',
      name: 'Cadran Solaire',
      type: 'static',
      modelId: 'sundial',
      position: [10, 0, 18],
      rotation: [0, 0, 0],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Gazebo
    {
      id: 'obj_gazebo',
      name: 'Gazebo',
      type: 'static',
      modelId: 'gazebo',
      position: [20, 0, 20],
      rotation: [0, Math.PI / 4, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {},
    },

    // Montre à gousset géante (horloge cassée)
    {
      id: 'obj_pocket_watch',
      name: 'Montre Géante',
      type: 'interactive',
      modelId: 'broken_clock',
      position: [22, 0, 10],
      rotation: [0, -0.5, 0],
      scale: [2, 2, 2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { broken: true },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'L\'horloge est figée à 6 heures. L\'heure du thé... toujours l\'heure du thé.',
          },
        },
      ],
    },

    // Tasses volantes (en spectral)
    {
      id: 'obj_flying_teacup_1',
      name: 'Tasse Volante',
      type: 'static',
      modelId: 'flying_teacup',
      position: [8, 3, 10],
      rotation: [0.2, 0.5, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { floats: true },
    },
    {
      id: 'obj_flying_teacup_2',
      name: 'Tasse Volante',
      type: 'static',
      modelId: 'flying_teacup',
      position: [15, 4, 8],
      rotation: [-0.1, 1.2, 0.15],
      scale: [1.2, 1.2, 1.2],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: { floats: true },
    },

    // Topiary (animaux taillés dans les buissons)
    {
      id: 'obj_topiary_rabbit',
      name: 'Topiary Lapin',
      type: 'static',
      modelId: 'topiary',
      position: [3, 0, 18],
      rotation: [0, Math.PI / 6, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { shape: 'rabbit' },
    },
    {
      id: 'obj_topiary_flamingo',
      name: 'Topiary Flamant',
      type: 'static',
      modelId: 'topiary',
      position: [21, 0, 14],
      rotation: [0, -Math.PI / 4, 0],
      scale: [1.8, 1.8, 1.8],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { shape: 'flamingo' },
    },

    // Porte étrange (menant nulle part)
    {
      id: 'obj_strange_door',
      name: 'Porte Mystérieuse',
      type: 'interactive',
      modelId: 'strange_door',
      position: [1, 0, 12],
      rotation: [0, Math.PI / 2, 0],
      scale: [0.3, 0.5, 0.3],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { tiny: true },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Une minuscule porte... Tu es bien trop grande pour passer !',
          },
        },
      ],
    },

    // Fiole "Drink Me"
    {
      id: 'obj_potion_drink_me',
      name: 'Fiole "Bois-moi"',
      type: 'interactive',
      modelId: 'potion_bottle',
      position: [9, 0.3, 14],
      rotation: [0, 0, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { label: 'BOIS-MOI' },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: '"BOIS-MOI" dit l\'étiquette... Peut-être vaut-il mieux ne pas y toucher.',
          },
        },
      ],
    },

    // Soldats-cartes (garde endormi)
    {
      id: 'obj_card_soldier',
      name: 'Soldat de Cartes',
      type: 'static',
      modelId: 'card_soldier',
      position: [22, 0, 5],
      rotation: [0, Math.PI, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: { sleeping: true },
    },

    // Plus de champignons
    {
      id: 'obj_mushroom_3',
      name: 'Petit Champignon',
      type: 'static',
      modelId: 'mushroom',
      position: [6, 0, 20],
      rotation: [0, 0.8, 0],
      scale: [0.6, 0.6, 0.6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#ff6b6b' },
    },
    {
      id: 'obj_mushroom_4',
      name: 'Petit Champignon',
      type: 'static',
      modelId: 'mushroom',
      position: [14, 0, 6],
      rotation: [0, -0.3, 0],
      scale: [0.5, 0.5, 0.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#9b59b6' },
    },

    // Terrier du lapin
    {
      id: 'obj_rabbit_hole',
      name: 'Terrier du Lapin',
      type: 'interactive',
      modelId: 'rabbit_hole',
      position: [16, 0, 3],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Un trou sombre... C\'est par là que tu es arrivée, non ?',
          },
        },
      ],
    },

    // Portail discret retour vers le Sanctuaire (toujours visible)
    {
      id: 'obj_portal_back',
      name: 'Retour au Sanctuaire',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [2, 0, 2],
      rotation: [0, 0, 0],
      scale: [0.8, 0.8, 0.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#9d8cff',
        isActive: true,
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: { roomId: 'room_sanctuary' },
        },
      ],
    },

    // === LA REINE ET SA COUR (monde spectral) ===

    // La Reine de Coeur
    {
      id: 'obj_queen_of_hearts',
      name: 'La Reine de Coeur',
      type: 'interactive',
      modelId: 'npc_queen_of_hearts',
      position: [20, 0, 20],
      rotation: [0, -2.5, 0],
      scale: [1.3, 1.3, 1.3],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {
        isNPC: true,
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'La Reine: "QU\'ON LUI COUPE LA TÊTE !" *pause* "...Oh pardon, réflexe. Que fais-tu dans MON jardin ?"',
          },
        },
      ],
    },

    // Soldats Cartes
    {
      id: 'obj_card_soldier_1',
      name: 'Soldat Carte',
      type: 'static',
      modelId: 'card_soldier',
      position: [18, 0, 18],
      rotation: [0, -2, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_card_soldier_2',
      name: 'Soldat Carte',
      type: 'static',
      modelId: 'card_soldier',
      position: [22, 0, 18],
      rotation: [0, -3, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_card_soldier_3',
      name: 'Soldat Carte',
      type: 'static',
      modelId: 'card_soldier',
      position: [19, 0, 22],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },
    {
      id: 'obj_card_soldier_4',
      name: 'Soldat Carte',
      type: 'static',
      modelId: 'card_soldier',
      position: [21, 0, 22],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'spectral',
      interactableInWorld: 'spectral',
      properties: {},
    },

    // === ARBRES ET BUISSONS ===

    // Arbres (visibles dans les deux mondes)
    {
      id: 'obj_tree_1',
      name: 'Arbre',
      type: 'static',
      modelId: 'tree',
      position: [1, 0, 10],
      rotation: [0, 0.3, 0],
      scale: [1.5, 2, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'circle', size: [0.5] },
    },
    {
      id: 'obj_tree_2',
      name: 'Arbre',
      type: 'static',
      modelId: 'tree',
      position: [23, 0, 8],
      rotation: [0, 1.2, 0],
      scale: [1.8, 2.2, 1.8],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'circle', size: [0.5] },
    },
    {
      id: 'obj_tree_3',
      name: 'Arbre',
      type: 'static',
      modelId: 'tree',
      position: [5, 0, 22],
      rotation: [0, 2.1, 0],
      scale: [1.6, 1.9, 1.6],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'circle', size: [0.5] },
    },
    {
      id: 'obj_tree_4',
      name: 'Arbre',
      type: 'static',
      modelId: 'tree',
      position: [15, 0, 23],
      rotation: [0, 0.8, 0],
      scale: [1.4, 1.7, 1.4],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
      collider: { type: 'circle', size: [0.5] },
    },

    // Buissons
    {
      id: 'obj_bush_1',
      name: 'Buisson',
      type: 'static',
      modelId: 'bush',
      position: [3, 0, 5],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_bush_2',
      name: 'Buisson',
      type: 'static',
      modelId: 'bush',
      position: [21, 0, 5],
      rotation: [0, 0.5, 0],
      scale: [1.2, 1, 1.2],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_bush_3',
      name: 'Buisson',
      type: 'static',
      modelId: 'bush',
      position: [10, 0, 21],
      rotation: [0, 1.3, 0],
      scale: [0.9, 0.8, 0.9],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },
    {
      id: 'obj_bush_4',
      name: 'Buisson',
      type: 'static',
      modelId: 'bush',
      position: [1, 0, 18],
      rotation: [0, 2.2, 0],
      scale: [1.1, 1.1, 1.1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {},
    },

    // Roses rouges (typiques de Wonderland)
    {
      id: 'obj_rose_bush_1',
      name: 'Rosier',
      type: 'static',
      modelId: 'rose_bush',
      position: [17, 0, 19],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#ff0040' },
    },
    {
      id: 'obj_rose_bush_2',
      name: 'Rosier',
      type: 'static',
      modelId: 'rose_bush',
      position: [23, 0, 19],
      rotation: [0, 0.5, 0],
      scale: [1, 1, 1],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: { color: '#ff0040' },
    },

    // Portail de victoire - retour au Sanctuaire après avoir parlé au Chapelier spectral
    {
      id: 'obj_victory_portal',
      name: 'Retour Triomphal au Sanctuaire',
      type: 'interactive',
      modelId: 'runic_sphere_portal',
      position: [12, 0, 1],
      rotation: [0, 0, 0],
      scale: [1.5, 1.5, 1.5],
      visibleInWorld: 'both',
      interactableInWorld: 'both',
      properties: {
        portalTo: 'room_sanctuary',
        portalColor: '#ffd700', // Doré pour le triomphe
        isActive: false,
        activateAfterRiddleSolved: 'hatter_spectral_first', // S'active après avoir parlé au Chapelier spectral
      },
      onInteract: [
        {
          type: 'loadRoom',
          params: {
            roomId: 'room_sanctuary',
            blockedMessage: 'Ce portail s\'ouvrira quand tu auras retrouvé le Chapelier dans le monde spectral !',
          },
        },
      ],
    },

    // L'Oracle qui apparaît
    {
      id: 'obj_oracle_appearance',
      name: 'L\'Oracle Chevelu',
      type: 'interactive',
      modelId: 'npc_oracle_chevelu',
      position: [3, 0, 14],
      rotation: [0, 0.5, 0],
      scale: [0.9, 0.9, 0.9],
      visibleInWorld: 'real',
      interactableInWorld: 'real',
      properties: {
        isNPC: true,
        dialogueId: 'oracle_wonderland_hint',
      },
      onInteract: [
        {
          type: 'showMessage',
          params: {
            message: 'Oracle: "Une vision me vient ! Le Chapelier a besoin... de se sentir en sécurité !" *pause* "...Oui, je sais, c\'était évident."',
          },
        },
      ],
    },
  ],
  puzzles: [
    // Puzzle principal: La Tea Party Progressive
    {
      id: 'puzzle_tea_party',
      name: 'La Tea Party Progressive',
      type: 'placement',
      state: 'active',
      description: 'Aide le Chapelier à organiser une mini tea party sans le submerger.',
      hint: 'Place les éléments un par un, en attendant entre chaque.',
      linkedObjectIds: ['obj_teacup_1', 'obj_tea_table_sad'],
      solution: {
        type: 'sequence',
        expected: ['teacup', 'saucer', 'teapot', 'cake'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Le Chapelier sourit... pour la première fois depuis longtemps !',
          },
        },
      ],
    },
    // Collecter les 3 souvenirs
    {
      id: 'puzzle_collect_memories',
      name: 'Les Souvenirs Heureux',
      type: 'collect',
      state: 'active',
      description: 'Trouve les 3 souvenirs heureux du Chapelier.',
      linkedObjectIds: ['obj_memory_photo_1', 'obj_memory_photo_2', 'obj_cheshire_cat'],
      solution: {
        type: 'items',
        expected: ['memory_photo_1', 'memory_photo_2', 'memory_photo_3'],
      },
      onSolve: [
        {
          type: 'showMessage',
          params: {
            message: 'Tu as rassemblé tous les souvenirs heureux du Chapelier !',
          },
        },
      ],
    },
  ],
  connections: [
    {
      targetRoomId: 'room_sanctuary',
      position: [12, 0, 1],
      direction: 'north',
      locked: false,
    },
  ],
  npcs: [
    // Chat du Cheshire (pettable)
    {
      id: 'npc_cheshire',
      type: 'fox', // Utilise fox comme base
      name: 'Chat du Cheshire',
      position: [8, 0, 18],
      scale: 1.2,
      idleAnimation: 'sit',
      petAnimation: 'happy',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Lapin Blanc
    {
      id: 'npc_white_rabbit',
      type: 'rabbit',
      name: 'Lapin Blanc',
      position: [16, 0, 10],
      scale: 1,
      idleAnimation: 'stand',
      petAnimation: 'jump',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Chenille (sur son champignon)
    {
      id: 'npc_caterpillar',
      type: 'lizard', // Utilise lizard comme base
      name: 'Chenille',
      position: [4, 1.5, 8],
      scale: 0.8,
      idleAnimation: 'sit',
      petAnimation: 'nuzzle',
      petRadius: 2,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
    // Flamant rose
    {
      id: 'npc_flamingo',
      type: 'heron', // Utilise heron comme base
      name: 'Flamant Rose',
      position: [18, 0, 14],
      scale: 1.3,
      idleAnimation: 'stand',
      petAnimation: 'happy',
      petRadius: 2.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'real',
    },
    // Hérisson
    {
      id: 'npc_hedgehog',
      type: 'hedgehog',
      name: 'Hérisson',
      position: [7, 0, 3],
      scale: 0.8,
      idleAnimation: 'sit',
      petAnimation: 'nuzzle',
      petRadius: 1.5,
      petCooldown: 3000,
      isPettable: true,
      visibleInWorld: 'both',
    },
  ],
};
